import type {
  ResourceDownloadResponse,
  SessionResource,
  SessionResourceFolder,
  SessionResourceLibrary,
} from '@lp/shared';
import { api } from './api.js';

/**
 * The reference library arrives flat — folders and resources each in one
 * list — and both the console and the learner page walk it as a tree. The
 * top level is the `null` folder.
 */

export function childrenOf(
  library: SessionResourceLibrary,
  folderId: string | null,
): { folders: SessionResourceFolder[]; resources: SessionResource[] } {
  return {
    folders: library.folders
      .filter((f) => f.parentId === folderId)
      .sort((a, b) => a.orderIndex - b.orderIndex || a.name.localeCompare(b.name)),
    resources: library.resources
      .filter((r) => r.folderId === folderId)
      .sort((a, b) => a.orderIndex - b.orderIndex),
  };
}

/** The folders from the top level down to `folderId`, inclusive. */
export function pathTo(library: SessionResourceLibrary, folderId: string | null): SessionResourceFolder[] {
  const byId = new Map(library.folders.map((f) => [f.id, f]));
  const path: SessionResourceFolder[] = [];
  let current = folderId ? byId.get(folderId) : undefined;
  while (current && path.length < 10) {
    path.unshift(current);
    current = current.parentId ? byId.get(current.parentId) : undefined;
  }
  return path;
}

/** Every folder id at or below `folderId`. */
export function subtreeIds(library: SessionResourceLibrary, folderId: string): Set<string> {
  const out = new Set<string>([folderId]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const f of library.folders) {
      if (f.parentId && out.has(f.parentId) && !out.has(f.id)) {
        out.add(f.id);
        grew = true;
      }
    }
  }
  return out;
}

/** How many files and links sit anywhere inside a folder. */
export function itemsWithin(library: SessionResourceLibrary, folderId: string): number {
  const ids = subtreeIds(library, folderId);
  return library.resources.filter((r) => r.folderId && ids.has(r.folderId)).length;
}

/** How many levels a folder spans, itself included. */
export function subtreeHeight(library: SessionResourceLibrary, folderId: string): number {
  const folder = library.folders.find((f) => f.id === folderId);
  if (!folder) return 1;
  const ids = subtreeIds(library, folderId);
  const deepest = Math.max(
    ...library.folders.filter((f) => ids.has(f.id)).map((f) => f.depth),
  );
  return deepest - folder.depth + 1;
}

/**
 * Download a reference file. The link is minted on click, so it is never
 * stale however long the page has been open, and the storage response forces
 * a download rather than opening the file in the browser.
 */
export async function downloadResource(resourceId: string): Promise<void> {
  const { url } = await api.get<ResourceDownloadResponse>(
    `/reference-docs/${encodeURIComponent(resourceId)}/download`,
  );
  window.location.assign(url);
}
